import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { FiltersTypes } from "@common/types/filters/general";
import { InvoiceSource } from "@common/types/invoice";
import { InvoiceTemplateListItem } from "@common/schemas/domains/invoice/invoice-template.schema";
import {
    DEFAULT_INVOICE_TEMPLATE_SORT,
    InvoiceTemplateArchivedFilter,
    InvoiceTemplateSort,
} from "@common/types/invoiceTemplate";
import { View } from "@common/types/views/views";
import {
    InvoiceTemplateView,
    InvoiceTemplateViewSchema,
} from "@common/types/views/invoiceTemplateViews";
import {
    useArchiveInvoiceTemplate,
    useDeleteInvoiceTemplate,
    useInvoiceTemplatesForEntities,
    useUnarchiveInvoiceTemplate,
} from "@api/invoiceTemplate.api";
import { useEntities } from "@hooks/useEntities";
import {
    WORKSPACE_USER_CACHE_KEYS,
    useWorkspaceUserCache,
} from "@hooks/useWorkspaceUserCache";
import { useOverlays } from "contexts/OverlayContext";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { isErrorToastedByBackendClient } from "@lib/backendClient";
import { InvoiceTemplatesEmptyView } from "./InvoiceTemplatesEmptyView";
import { InvoiceTemplatesListFooter } from "./InvoiceTemplatesListFooter";
import { InvoiceTemplatesListHeader } from "./InvoiceTemplatesListHeader";
import { useInvoiceTemplateBulkActions } from "./useInvoiceTemplateBulkActions";
import {
    InvoiceTemplateRow,
    InvoiceTemplatesTableCallbacks,
    InvoiceTemplatesTableMeta,
} from "./invoiceTemplates.types";
import {
    defaultInvoiceTemplateView,
    invoiceTemplatesViewConfig,
} from "./invoiceTemplatesViewConfig";

const TEMPLATES_PAGE_SIZE = 50;

export const InvoiceTemplatesList: React.FC = () => {
    const { error: errorToast } = useToaster();
    const { openOverlay } = useOverlays();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState<
        Pick<
            FiltersTypes,
            | "entityIds"
            | "counterpartiesIds"
            | "withEmptyOrUnverifiedCounterparty"
            | "hasCounterparty"
            | "invoiceTemplateCreatedByUserIds"
        >
    >({});
    const [archivedFilter, setArchivedFilter] =
        useState<InvoiceTemplateArchivedFilter>(
            InvoiceTemplateArchivedFilter.ACTIVE,
        );
    const [page, setPage] = useState(1);

    const businessEntities = useEntities({ onlyBusiness: true });
    const selectedEntityIds = filters.entityIds ?? [];
    const isAllEntitiesSelected =
        selectedEntityIds.length === 0 ||
        (businessEntities.length > 0 &&
            businessEntities.every((entity) =>
                selectedEntityIds.includes(entity.id),
            ));
    const entityIds = isAllEntitiesSelected
        ? businessEntities.map((entity) => entity.id)
        : selectedEntityIds;

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<InvoiceTemplateView>(
            WORKSPACE_USER_CACHE_KEYS.INVOICE_TEMPLATES_PAGE_VIEW_CACHE_KEY,
            InvoiceTemplateViewSchema,
        );
    const [view, setView] = useState<InvoiceTemplateView>(
        cachedView ?? defaultInvoiceTemplateView,
    );

    const sort = view.sort ?? DEFAULT_INVOICE_TEMPLATE_SORT;

    const { templates, total, pageCount, isLoading, isFetching } =
        useInvoiceTemplatesForEntities({
            entityIds,
            search: search || undefined,
            archivedFilter,
            counterpartiesIds: filters.counterpartiesIds,
            withEmptyOrUnverifiedCounterparty:
                filters.withEmptyOrUnverifiedCounterparty,
            hasCounterparty: filters.hasCounterparty,
            createdByUserIds: filters.invoiceTemplateCreatedByUserIds,
            sort,
            page,
            limit: TEMPLATES_PAGE_SIZE,
        });

    // entityIds / counterpartiesIds are recomputed (new array reference) on
    // every render, so key on their content instead — otherwise this effect
    // would reset to page 1 on every render rather than only on an actual
    // filter change.
    const entityIdsKey = entityIds.join(",");
    const counterpartiesIdsKey = (filters.counterpartiesIds ?? []).join(",");
    const createdByUserIdsKey = (
        filters.invoiceTemplateCreatedByUserIds ?? []
    ).join(",");

    // Reset to page 1 whenever a filter/search input narrows or reorders the
    // matching set, so the user doesn't land on a now out-of-range page.
    useEffect(() => {
        setPage(1);
    }, [
        search,
        entityIdsKey,
        archivedFilter,
        counterpartiesIdsKey,
        filters.withEmptyOrUnverifiedCounterparty,
        filters.hasCounterparty,
        createdByUserIdsKey,
        sort,
    ]);

    // Clamp the current page back into range when the result set shrinks
    // (e.g. archiving the last template on the final page lowers pageCount),
    // so the user isn't stranded on an empty out-of-range page. Use a
    // functional update so that when a filter change fires the reset effect
    // and this clamp in the same commit (cached result with fewer pages), the
    // clamp reads the reset's queued page 1 instead of the stale page and
    // doesn't override the reset back to the last filtered page.
    useEffect(() => {
        setPage((prev) => (prev > pageCount ? pageCount : prev));
    }, [pageCount]);

    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const clearSelection = useCallback(() => setRowSelection({}), []);

    // Selection is keyed by row id from the current (server-paginated) page,
    // so resolving through `templates` stays cheap and avoids holding onto
    // stale rows from other pages.
    const pageTemplatesById = useMemo(
        () => new Map(templates.map((template) => [template.id, template])),
        [templates],
    );

    // Selection is per-page. When pagination, filtering, or sorting changes
    // the visible rows, drop any selected ids that left the current page so a
    // hidden selection can't silently reactivate — and later be bulk-acted on
    // — when the user navigates back to those rows.
    useEffect(() => {
        setRowSelection((prev) => {
            const pruned = Object.fromEntries(
                Object.entries(prev).filter(
                    ([id, selected]) => selected && pageTemplatesById.has(id),
                ),
            );
            return Object.keys(pruned).length === Object.keys(prev).length
                ? prev
                : pruned;
        });
    }, [pageTemplatesById]);

    const selectedRows = useMemo(
        () =>
            Object.entries(rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => pageTemplatesById.get(id))
                .filter(
                    (template): template is InvoiceTemplateRow => !!template,
                ),
        [rowSelection, pageTemplatesById],
    );

    const { handleOpenBulkActions, isBulkActionPending } =
        useInvoiceTemplateBulkActions({
            selectedRows,
            clearSelection,
        });

    const archiveTemplate = useArchiveInvoiceTemplate();
    const unarchiveTemplate = useUnarchiveInvoiceTemplate();
    const deleteTemplate = useDeleteInvoiceTemplate();

    const handleCreateNew = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("nativeInvoiceDrawer", { createTemplate: true });
    }, [openOverlay, refuseIfViewOnly]);

    const handleCreateFromInvoice = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        openOverlay("invoicesDrilldown", {
            filters: { invoiceSources: [InvoiceSource.MANUAL] },
            selectForTemplate: true,
        });
    }, [openOverlay, refuseIfViewOnly]);

    const handleEdit = useCallback(
        (template: InvoiceTemplateListItem) => {
            openOverlay("nativeInvoiceDrawer", {
                editTemplate: {
                    templateId: template.id,
                    entityId: template.entityId,
                },
            });
        },
        [openOverlay],
    );

    const handleCreateInvoice = useCallback(
        (template: InvoiceTemplateListItem) => {
            openOverlay("nativeInvoiceDrawer", {
                applyTemplate: {
                    templateId: template.id,
                    entityId: template.entityId,
                },
            });
        },
        [openOverlay],
    );

    // Opens the same create-template drawer used for "Create template", but
    // pre-filled from this template's content, so saving it produces a brand
    // new, independent template rather than mutating the source.
    const handleDuplicate = useCallback(
        (template: InvoiceTemplateListItem) => {
            if (refuseIfViewOnly()) {
                return;
            }
            openOverlay("nativeInvoiceDrawer", {
                duplicateTemplate: {
                    templateId: template.id,
                    entityId: template.entityId,
                },
            });
        },
        [openOverlay, refuseIfViewOnly],
    );

    const handleArchiveToggle = useCallback(
        async (template: InvoiceTemplateListItem) => {
            try {
                if (template.archivedAt) {
                    await unarchiveTemplate.mutateAsync({
                        entityId: template.entityId,
                        templateId: template.id,
                    });
                    return;
                }
                await archiveTemplate.mutateAsync({
                    entityId: template.entityId,
                    templateId: template.id,
                });
            } catch (error) {
                if (!isErrorToastedByBackendClient(error)) {
                    errorToast(
                        `Couldn't ${template.archivedAt ? "enable" : "archive"} ${template.name}`,
                    );
                }
            }
        },
        [archiveTemplate, unarchiveTemplate, errorToast],
    );

    const handleDelete = useCallback(
        async (template: InvoiceTemplateListItem) => {
            // The API client surfaces the backend error message (e.g. the 409
            // "...archive it instead" for used templates), so we intentionally
            // don't toast a second, hard-coded message when a `data.message` is
            // present. Cover every gap it can't (network failures with no
            // response, or an HTTP error whose body has no message) so a
            // failed delete never goes unreported.
            try {
                await deleteTemplate.mutateAsync({
                    entityId: template.entityId,
                    templateId: template.id,
                });
            } catch (error) {
                if (!isErrorToastedByBackendClient(error)) {
                    errorToast(`Failed to delete ${template.name}`);
                }
            }
        },
        [deleteTemplate, errorToast],
    );

    const hasActiveFilters =
        search.trim().length > 0 ||
        !isAllEntitiesSelected ||
        archivedFilter !== InvoiceTemplateArchivedFilter.ACTIVE ||
        !!filters.counterpartiesIds?.length ||
        !!filters.withEmptyOrUnverifiedCounterparty ||
        !!filters.hasCounterparty ||
        !!filters.invoiceTemplateCreatedByUserIds?.length;

    const handleClearFilters = useCallback(() => {
        setSearch("");
        setFilters({});
        setArchivedFilter(InvoiceTemplateArchivedFilter.ACTIVE);
    }, []);

    // ViewTable's onViewChange fires with whatever `view` it's currently
    // rendering (i.e. viewWithBulkActions below), so strip the injected
    // selection/bulkActions overrides before parsing/persisting — otherwise
    // they'd get baked into the cached view on every resize/sort/column
    // change.
    const handleViewChange = useCallback(
        (newView: View) => {
            const result = InvoiceTemplateViewSchema.safeParse({
                ...newView,
                options: {
                    ...newView.options,
                    selection: false,
                    bulkActions: false,
                },
            });
            const parsedView = result.success
                ? result.data
                : defaultInvoiceTemplateView;
            setView(parsedView);
            setCachedView(parsedView);
        },
        [setCachedView],
    );

    // Always render the selection checkbox column so bulk actions are
    // discoverable, regardless of the cached view's persisted options.
    const viewWithBulkActions = useMemo<InvoiceTemplateView>(
        () => ({
            ...view,
            options: {
                ...view.options,
                selection: true,
                bulkActions: true,
            },
        }),
        [view],
    );

    const handleSortChange = useCallback(
        (newSort: InvoiceTemplateSort) => {
            const nextView: InvoiceTemplateView = { ...view, sort: newSort };
            handleViewChange(nextView);
        },
        [handleViewChange, view],
    );

    const handleSortRemove = useCallback(() => {
        const nextView: InvoiceTemplateView = {
            ...view,
            sort: DEFAULT_INVOICE_TEMPLATE_SORT,
        };
        handleViewChange(nextView);
    }, [handleViewChange, view]);

    const callbacks = useMemo<InvoiceTemplatesTableCallbacks>(
        () => ({
            onCreateInvoice: handleCreateInvoice,
            onEdit: handleEdit,
            onDuplicate: handleDuplicate,
            onArchiveToggle: (template) => {
                void handleArchiveToggle(template);
            },
            onDelete: (template) => {
                void handleDelete(template);
            },
        }),
        [
            handleCreateInvoice,
            handleEdit,
            handleDuplicate,
            handleArchiveToggle,
            handleDelete,
        ],
    );

    const tableMeta = useMemo<InvoiceTemplatesTableMeta>(
        () => ({ callbacks }),
        [callbacks],
    );

    const emptyView = useCallback(
        () => (
            <InvoiceTemplatesEmptyView
                variant={hasActiveFilters ? "filtered" : "empty"}
                onClearFilters={handleClearFilters}
                onCreateNew={handleCreateNew}
                onCreateFromInvoice={handleCreateFromInvoice}
            />
        ),
        [
            hasActiveFilters,
            handleClearFilters,
            handleCreateNew,
            handleCreateFromInvoice,
        ],
    );

    const handleRowClick = useCallback(
        (row?: InvoiceTemplateRow) => {
            if (row) {
                handleEdit(row);
            }
        },
        [handleEdit],
    );

    const selectedCount = selectedRows.length;

    const footer = useMemo(
        () => (
            <InvoiceTemplatesListFooter
                total={total}
                currentPage={page}
                totalPages={pageCount}
                onPageChange={setPage}
                isLoading={isFetching}
                selectedCount={selectedCount}
                onClearSelection={clearSelection}
                onOpenBulkActions={handleOpenBulkActions}
                isBulkActionPending={isBulkActionPending}
            />
        ),
        [
            total,
            page,
            pageCount,
            isFetching,
            selectedCount,
            clearSelection,
            handleOpenBulkActions,
            isBulkActionPending,
        ],
    );

    return (
        <main>
            <InvoiceTemplatesListHeader
                search={search}
                setSearch={setSearch}
                filters={filters}
                onFiltersChange={(nextFilters) =>
                    setFilters((prev) => ({ ...prev, ...nextFilters }))
                }
                archivedFilter={archivedFilter}
                onArchivedFilterChange={setArchivedFilter}
                view={view}
                onViewChange={handleViewChange}
                onCreateNew={handleCreateNew}
                onCreateFromInvoice={handleCreateFromInvoice}
            />
            <ViewTable
                data={isLoading && templates.length === 0 ? null : templates}
                view={viewWithBulkActions}
                config={invoiceTemplatesViewConfig}
                sort={sort}
                onSortChange={handleSortChange}
                onSortRemove={handleSortRemove}
                onViewChange={handleViewChange}
                onRowClick={handleRowClick}
                rowBorders={false}
                isParentSelectable={false}
                fixedTopOffsetPx
                tableMeta={tableMeta}
                emptyView={emptyView}
                footer={footer}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
            />
        </main>
    );
};
